// 独立站 AI 流水线 demo 主入口
// URL: http://localhost:8080/idp-ai-demo
// 完全独立 mount，不走 Layout/Login/menus/PageContainer 系统

import React, { useState } from 'react';
import { Layout, Menu, Tag, Space, Typography } from 'antd';
import {
  AppstoreOutlined, FileSearchOutlined,
  AuditOutlined, LineChartOutlined, ExperimentOutlined, MedicineBoxOutlined,
  EditOutlined, BulbOutlined,
} from '@ant-design/icons';

import CandidateBoard from './pages/CandidateBoard';
import CandidateDetail from './pages/CandidateDetail';
import ReviewWorkstation from './pages/ReviewWorkstation';
import TrafficAnalysis from './pages/TrafficAnalysis';
import ExperimentCenter from './pages/ExperimentCenter';
import DataHealth from './pages/DataHealth';
import ExperimentEditor from './pages/ExperimentEditor';
import FeedbackLearning from './pages/FeedbackLearning';

const { Header, Sider, Content } = Layout;

const IdpAiDemo: React.FC = () => {
  const [activeKey, setActiveKey] = useState<string>('board');
  const [drawerCandidateId, setDrawerCandidateId] = useState<string | null>(null);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#001529',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
          🚀 独立站 AI 流水线
        </Typography.Title>
        <Tag color="processing">Demo · 流量页 API + Mock 兜底</Tag>
        <Space style={{ marginLeft: 'auto', color: '#fff' }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
            柴进 + Claude · 2026-06-27 ·
          </span>
          <span style={{ fontSize: 12, color: '#bfbfbf' }}>
            14 表 mock · 流量/健康接 gateway · spec §4.9 全覆盖
          </span>
        </Space>
      </Header>
      <Layout>
        <Sider width={200} theme="light">
          <Menu
            mode="inline"
            selectedKeys={[activeKey]}
            onClick={({ key }) => setActiveKey(String(key))}
            style={{ height: '100%', borderRight: 0 }}
          >
            <Menu.Item key="board" icon={<AppstoreOutlined />}>① AI 候选看板</Menu.Item>
            <Menu.Item key="review" icon={<AuditOutlined />}>② Review 工作台</Menu.Item>
            <Menu.Item key="traffic" icon={<LineChartOutlined />}>③ 流量分析</Menu.Item>
            <Menu.Item key="experiment" icon={<ExperimentOutlined />}>④ 实验中心</Menu.Item>
            <Menu.Item key="health" icon={<MedicineBoxOutlined />}>⑤ 数据健康</Menu.Item>
            <Menu.Item key="editor" icon={<EditOutlined />}>⑥ 实验录入（占位）</Menu.Item>
            <Menu.Item key="feedback" icon={<BulbOutlined />}>⑦ 反馈学习</Menu.Item>
            <Menu.Item key="about" icon={<FileSearchOutlined />}>关于此 demo</Menu.Item>
          </Menu>
        </Sider>
        <Content style={{ background: '#f5f5f5', overflow: 'auto' }}>
          {activeKey === 'board' && (
            <CandidateBoard onOpenDetail={(id) => setDrawerCandidateId(id)} />
          )}
          {activeKey === 'review' && <ReviewWorkstation />}
          {activeKey === 'traffic' && <TrafficAnalysis />}
          {activeKey === 'experiment' && <ExperimentCenter />}
          {activeKey === 'health' && <DataHealth />}
          {activeKey === 'editor' && <ExperimentEditor />}
          {activeKey === 'feedback' && <FeedbackLearning />}
          {activeKey === 'about' && (
            <div style={{ padding: 32 }}>
              <Typography.Title level={3}>关于此 demo</Typography.Title>
              <Typography.Paragraph>
                这是<strong>独立站 AI 全流程实施计划</strong>的可视化 demo,
                覆盖飞书文档 §5 数据架构的 <strong>14 张表</strong>（AI 候选 7 + 流量监控 7）
                和 <strong>10 状态机 + 9 步链路</strong>的完整呈现。
              </Typography.Paragraph>
              <Typography.Title level={5}>1. 覆盖的表（左侧菜单序号对应）</Typography.Title>
              <Typography.Paragraph>
                <strong>AI 候选 7 表（saas_analytics + saas_edata + saas_item）</strong>：
                <ul>
                  <li><code>ai_candidate</code> — 候选主表（10 状态机）</li>
                  <li><code>ai_candidate_ref</code> — 逻辑引用（不拉强外键）</li>
                  <li><code>ai_candidate_decision</code> — 决策审计</li>
                  <li><code>ai_candidate_quality</code> — 图候选质量打分 + 拒图标签</li>
                  <li><code>ai_listing_draft</code> — listing 草稿版本</li>
                  <li><code>ai_price_recommendation</code> — 三档价 + 相似款依据</li>
                  <li><code>ai_candidate_effect_daily</code> — 效果时序</li>
                </ul>
                <strong>流量监控 7 表（saas_analytics）</strong>：
                <ul>
                  <li><code>idp_event</code> — 事件流（页面/点击/滚动/加购/购买/询单）</li>
                  <li><code>idp_page</code> — 页面维表</li>
                  <li><code>idp_element</code> — 元素维表</li>
                  <li><code>idp_experiment</code> + <code>idp_variant</code> + <code>idp_exposure</code> — A/B 实验三表</li>
                  <li><code>page_daily</code> / <code>click_daily</code> / <code>scroll_daily</code> / <code>experiment_daily</code> — rollup 表</li>
                </ul>
              </Typography.Paragraph>
              <Typography.Title level={5}>2. 关键交互（验功能点）</Typography.Title>
              <Typography.Paragraph>
                <ol>
                  <li><strong>候选看板</strong>：看板 vs 列表两种视图；筛选风格/站点；点卡片打开详情抽屉</li>
                  <li><strong>候选详情</strong>：10 状态机进度条 + 9 步端到端链路图 + 7 表数据 tabs</li>
                  <li><strong>Review 工作台</strong>：左侧待审列表 + 中间图候选挑选（CV 粗筛 + 一键选/拒）+ 右侧审计流；点"拒"弹出原因 modal（9 选 1 + 自由文本，结构化采集）</li>
                  <li><strong>流量分析</strong>：5 tab — 总览（DAU/WAU/MAU + 三层身份）/ 会话与路径（单 session timeline + 路径分析 + 退出页 + 跨站 device）/ 漏斗与留存（多步漏斗 + Cohort 矩阵）/ 热区（viewport 桶 + 完读率）/ 实时事件流</li>
                  <li><strong>实验中心</strong>：4 tab — 实验列表 / 曝光与分桶（UNIQUE(experiment_id, device_id) 首曝快照 + 分桶质量校验）/ 显著性面板（p-value + 置信区间 + MDE + Power）/ 实验×候选交叉</li>
                  <li><strong>数据健康</strong>：spec §4.9 数字化验收 4 项 + rollup 延迟 SLA + 字段填充率 + 月分区 + bot 过滤 + 维表 cache + sendBeacon 队列</li>
                </ol>
              </Typography.Paragraph>

              <Typography.Title level={5}>3. spec §4.9 锚点（流量监控部分）</Typography.Title>
              <Typography.Paragraph>
                <ul>
                  <li><strong>三决策（柴进 2026-06-12 拍板）</strong>：① 存储=MySQL（不加新数据源）② 落 saas_analytics 库 ③ click 全采（Clarity 模式）</li>
                  <li><strong>三层身份</strong>：device_id ← Shopify <code>_shopify_y</code>（1y）/ session_id ← <code>_shopify_s</code>（30min 切）/ customer_id（登录后）</li>
                  <li><strong>接口契约</strong>：<code>POST /idp/events</code>（无鉴权 + 限流 + CORS 3 站，body ≤50 events/请求，sendBeacon 兼容）/ <code>GET /idp/experiments/config</code>（强缓存）</li>
                  <li><strong>分桶</strong>：客户端 hash(device_id, exp_key) 确定性产生 → 同 device 7 天 100% 同桶</li>
                  <li><strong>写路径</strong>：批量数组 POST → 维表 ID 解析 cache+ON DUPLICATE → saveAll，无回查无 UPDATE，删 PUT 端点</li>
                  <li><strong>数字化验收 4 项</strong>（上线 1mo 后必达）：PV 准确率 ≥99% / device_id 7d 持久度 ≥95% / click 落库 ≥99% / 实验分桶一致性 100%</li>
                </ul>
              </Typography.Paragraph>
              <Typography.Title level={5}>4. 不实现的（demo 边界）</Typography.Title>
              <Typography.Paragraph>
                <ul>
                  <li>流量分析 / 数据健康已接 gateway 查询 API；其余"提交后端"动作仍是 message 提示</li>
                  <li>图片是 unsplash 占位图，不是真实 saas-image-service 出图</li>
                  <li>事件流 1500 条 mock，不是真实 1 天百万级</li>
                  <li>无用户认证（任何人开 URL 都能进）</li>
                </ul>
              </Typography.Paragraph>
            </div>
          )}
        </Content>
      </Layout>

      <CandidateDetail candidateId={drawerCandidateId} onClose={() => setDrawerCandidateId(null)} />
    </Layout>
  );
};

export default IdpAiDemo;
